import { createContext, useState, useEffect } from 'react';
import instance from '../axios/axios';
import useAuth from '../hooks/useAuth';

const GlobalContext = createContext();

export const GlobalProvider = ({ children }) => {
    const [navItems, setNavItems] = useState([]);
    const [hierarchicalReports, setHierarchicalReports] = useState([]);
    const [apps, setApps] = useState([]);
    const [anchorElReports, setAnchorElReports] = useState(null);
    const [anchorElApps, setAnchorElApps] = useState(null);
    const [anchorElDashboards, setAnchorElDashboards] = useState(null);
    const [dashboards, setDashboards] = useState([]);
    const { auth } = useAuth();

    const buildHierarchicalStructure = (reports) => {
        // Create a map for quick lookup
        const reportMap = new Map();
        reports.forEach(report => {
            reportMap.set(report.id, {
                ...report,
                children: [],
                isExpanded: false
            });
        });

        // Build parent-child relationships
        const topLevelReports = [];
        const childIds = new Set();

        reports.forEach(report => {
            const reportNode = reportMap.get(report.id);
            
            // Add children to this report
            if (report.child_report_info && report.child_report_info.length > 0) {
                report.child_report_info.forEach(childInfo => {
                    const childNode = reportMap.get(childInfo.child_report_id);
                    if (childNode) {
                        reportNode.children.push(childNode);
                        childIds.add(childInfo.child_report_id);
                    }
                });
            }
        });

        // Find top-level reports (those that are not children of any other report)
        reports.forEach(report => {
            if (!childIds.has(report.id)) {
                topLevelReports.push(reportMap.get(report.id));
            }
        });

        return topLevelReports;
    };

    const refreshNavigation = async () => {
        try {
            const response = await instance.get('/users/access/jwt/all', { withCredentials: true });
            if (response.status === 200) {
                const data = response.data.data;
                
                // Update reports - map the response format to match existing structure
                const reports = data.reports.map(report => ({
                    id: report.report_id,
                    name: report.report_name,
                    path: report.report_path,
                    required_params: report.report_required_params,
                    child_report_info: report.child_report_info || [],
                    parent_report_info: report.parent_report_info || []
                }));
                setNavItems(reports);
                
                // Build hierarchical structure from the same data
                const hierarchicalStructure = buildHierarchicalStructure(reports);
                setHierarchicalReports(hierarchicalStructure);
                
                // Update dashboards - map the response format to match existing structure
                const dashboards = data.dashboards.map(dashboard => ({
                    id: dashboard.dashboard_id,
                    name: dashboard.dashboard_name,
                    path: dashboard.dashboard_path,
                    chart_type: dashboard.dashboard_type
                }));
                setDashboards(dashboards);
                
                // Update applications - map the response format to match existing structure
                const applications = data.applications.map(app => ({
                    id: app.application_id,
                    name: app.application_name,
                    base_url: app.application_base_url
                }));
                setApps(applications);
            } else {
                console.error("Failed to get user access data");
            }
        } catch (error) {
            console.error(`Error fetching user access data: ${error}`);
        }
    };


    useEffect(() => {
        if (auth?.auth) {
            instance.get('/users/access_list_from_jwt', { withCredentials: true })
                .then((response) => {
                    if (response.status === 200) {
                        const accessList = response.data.data.access;
                        setNavItems(accessList);
                    } else {
                        console.error('Error getting access list');
                    }
                })
                .catch((error) => {
                    console.error('Error getting access list', error);
                });
        }
    }, [anchorElReports]);


    useEffect(() => {
        if (auth?.auth) {
            instance.get('/users/dashboards/access_list_from_jwt', { withCredentials: true })
                .then((response) => {
                    if (response.status === 200) {
                        const accessList = response.data.data.access;
                        setDashboards(accessList);
                    } else {
                        console.error('Error getting access list');
                    }
                })
                .catch((error) => {
                    console.error('Error getting access list', error);
                });
        }
    }, [anchorElReports]);


    useEffect(() => {
        const fetchApps = async () => {
            try {
                const response = await instance.get('/users/apps/access_list_from_jwt', { withCredentials: true });
                if (response.status === 200) {
                    setApps(response.data.data.access);
                }
            } catch (error) {
                console.error('Error getting applications', error);
            }
        };
        if (auth?.auth) {
            fetchApps();
        }
    }, [anchorElApps]);

    return (
        <GlobalContext.Provider
            value={{
                navItems,
                setNavItems,
                hierarchicalReports,
                setHierarchicalReports,
                apps,
                setApps,
                anchorElReports,
                setAnchorElReports,
                anchorElApps,
                setAnchorElApps,
                dashboards,
                setDashboards,
                anchorElDashboards,
                setAnchorElDashboards,
                refreshNavigation
            }}
        >
            {children}
        </GlobalContext.Provider>
    );
}

export default GlobalContext;
